import { useQuery } from '@tanstack/react-query';
import type { ApiErrorEnvelope, DownloadAuth, LocalUpdateStatusDto, PublicHome, PublicLandingDto, ReleaseChannel, UpdateLatestDto } from '@his/shared';
import { DOWNLOAD_AUTH_HEADERS, DOWNLOAD_AUTH_QUERY } from '@his/shared';
import { ApiError } from '@/lib/api';
import { publicGet } from '@/lib/public-api';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

export type { PublicHome, PublicHomeHospital, PublicHomeStats } from '@his/shared';

/**
 * The landing page's numbers, from `GET /api/v1/public/home`.
 *
 * Goes through the public client on purpose: the staff client would attach a
 * tenant header and a staff token to a request that must carry neither.
 */
export function usePublicHome() {
  return useQuery({
    queryKey: ['public', 'home'],
    queryFn: () => publicGet<PublicHome>('/home'),
    staleTime: 60_000,
    retry: 1,
  });
}

/* ------------------------- offline distribution -------------------------- */

/** `GET /api/v1/public/landing`: the cloud version and the latest stable / beta packages. */
export function usePublicLanding() {
  return useQuery({
    queryKey: ['public', 'landing'],
    queryFn: () => publicGet<PublicLandingDto>('/landing'),
    staleTime: 60_000,
    retry: 1,
  });
}

/**
 * Proves the hospital code + PIN before the browser is sent to the download
 * URL — so a wrong PIN is a message on the page, not a 401 page in a new tab.
 * A raw fetch, not the staff client: this must carry neither a tenant header
 * nor a staff token.
 */
export async function validateDownloadAccess(auth: DownloadAuth, channel: ReleaseChannel): Promise<UpdateLatestDto> {
  const response = await fetch(`${API_BASE}/updates/latest?channel=${encodeURIComponent(channel)}`, {
    headers: { [DOWNLOAD_AUTH_HEADERS.tenantSlug]: auth.tenantSlug, [DOWNLOAD_AUTH_HEADERS.pin]: auth.pin },
  });
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const envelope = payload as ApiErrorEnvelope | null;
    throw new ApiError(response.status, envelope?.error?.code ?? 'internal_error', envelope?.error?.message ?? response.statusText, envelope?.error?.details ?? []);
  }
  return payload as UpdateLatestDto;
}

/** The download itself is a plain navigation, so the credentials travel in the query string. */
export function browserDownloadUrl(version: string, auth: DownloadAuth): string {
  const params = new URLSearchParams({ [DOWNLOAD_AUTH_QUERY.tenantSlug]: auth.tenantSlug, [DOWNLOAD_AUTH_QUERY.pin]: auth.pin });
  return `${API_BASE}/updates/download/${encodeURIComponent(version)}?${params.toString()}`;
}

export function agentDownloadUrl(fileName: string): string {
  return `${API_BASE}/public/agents/${encodeURIComponent(fileName)}`;
}

/** "his.local:3001" → "http://his.local:3001"; null when it is not an address at all. */
export function normalizeServerAddress(raw: string): string | null {
  const trimmed = raw.trim().replace(/\/+$/, '');
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
  try {
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

/**
 * "Check my version": asks the hospital's own server, across origins, what
 * it runs. The local API opens CORS on exactly this route for the landing
 * page's origin (`LANDING_PAGE_ORIGIN`). Accepts a bare object or a `{ data }`
 * envelope, so an older or sibling-product server still parses.
 */
export async function fetchLocalServerStatus(address: string): Promise<LocalUpdateStatusDto> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8_000);
  try {
    const res = await fetch(`${address}/api/v1/updates/status`, { signal: controller.signal, mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const json = (await res.json()) as LocalUpdateStatusDto | { data?: LocalUpdateStatusDto };
    const status = 'data' in json && json.data ? json.data : (json as LocalUpdateStatusDto);
    if (typeof status.runningVersion !== 'string') throw new Error('unexpected response');
    return status;
  } finally {
    window.clearTimeout(timer);
  }
}
